import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "commitment", title: "Our Commitment" },
  { id: "standard", title: "Standard We Target" },
  { id: "implemented", title: "What's Implemented Today" },
  { id: "known", title: "Known Limitations" },
  { id: "assistive", title: "Assistive Technology" },
  { id: "report", title: "Report a Barrier" },
  { id: "review", title: "Review Cadence" },
];

const AccessibilityStatement = () => (
  <>
    <Helmet>
      <title>Accessibility — The Emerging Market</title>
      <meta
        name="description"
        content="How The Emerging Market builds and tests for accessibility, our WCAG 2.1 AA target, current implementation, and how to report a barrier."
      />
      <link rel="canonical" href="https://emrkt.co/accessibility" />
      <meta property="og:title" content="Accessibility — The Emerging Market" />
      <meta property="og:url" content="https://emrkt.co/accessibility" />
      <meta property="og:type" content="website" />
    </Helmet>
    <LegalLayout
      title="Accessibility Statement"
      description="What we do to keep the platform usable for everyone, what's still in progress, and how to reach us if something in the way."
      lastUpdated="July 2, 2026"
      sections={sections}
    >
      <section id="commitment">
        <h2>1. Our Commitment</h2>
        <p>
          The Emerging Market serves Alabama communities that already face
          extra friction reaching civic data. If our platform is inaccessible,
          it fails the people it's built for. Accessibility is treated as a
          working requirement of the product — not a compliance afterthought.
        </p>
      </section>

      <section id="standard">
        <h2>2. Standard We Target</h2>
        <p>
          We build against{" "}
          <a
            href="https://www.w3.org/WAI/WCAG21/quickref/?levels=aa"
            target="_blank"
            rel="noreferrer"
          >
            WCAG 2.1 Level AA
          </a>{" "}
          as our working target. We do not currently claim full conformance —
          we describe below what is implemented today and what we know is
          still incomplete.
        </p>
      </section>

      <section id="implemented">
        <h2>3. What's Implemented Today</h2>
        <ul>
          <li>
            <strong>Semantic HTML &amp; landmarks.</strong> Each route uses one
            main landmark; navigation, footer, and content regions are
            marked up with the correct elements.
          </li>
          <li>
            <strong>Keyboard navigation.</strong> Interactive elements are
            reachable via keyboard. Menus, dialogs, and popovers are built on
            Radix UI primitives that ship accessible focus management,
            escape-to-close, and ARIA out of the box.
          </li>
          <li>
            <strong>Accessible names on icon-only controls.</strong> Icon
            buttons carry <code>aria-label</code> so screen readers announce
            their purpose.
          </li>
          <li>
            <strong>Design-system contrast tokens.</strong> Body text,
            muted-text, primary, and destructive tokens are tuned to meet
            WCAG AA on our dark background rather than relying on arbitrary
            gray values.
          </li>
          <li>
            <strong>Form labels.</strong> Text inputs, selects, textareas,
            and switches are paired with visible labels or explicit{" "}
            <code>aria-label</code> attributes.
          </li>
          <li>
            <strong>Focus-visible indicators.</strong> Keyboard focus is
            visible on buttons, links, and form controls via the shared
            focus-ring token.
          </li>
          <li>
            <strong>Reduced-motion respect.</strong> Framer Motion animations
            fall back to instant transitions when a user has
            <code>prefers-reduced-motion</code> enabled at the OS level.
          </li>
          <li>
            <strong>Responsive &amp; zoomable.</strong> Layouts reflow at 320px
            width and remain usable at 200% browser zoom without horizontal
            scrolling.
          </li>
          <li>
            <strong>Alt text on content images.</strong> Article, idea, and
            profile images accept alt text at upload time; decorative images
            use empty alt attributes.
          </li>
        </ul>
      </section>

      <section id="known">
        <h2>4. Known Limitations</h2>
        <p>
          We're being honest about what still needs work:
        </p>
        <ul>
          <li>
            Some data-heavy tables (funding history, legislation timeline)
            do not yet announce sort or filter changes to screen readers.
          </li>
          <li>
            Charts and the interactive Alabama county map do not yet expose
            a text-based data alternative on the same page.
          </li>
          <li>
            A handful of legacy long-form articles imported from prior
            drafts may contain images without alt text; we're auditing on a
            rolling basis.
          </li>
          <li>
            Video content embedded from third parties (e.g. TikTok) inherits
            those providers' captioning — we can't guarantee captions on
            every clip.
          </li>
        </ul>
        <p>
          These items are tracked on our internal accessibility backlog and
          reviewed alongside every release.
        </p>
      </section>

      <section id="assistive">
        <h2>5. Assistive Technology</h2>
        <p>
          The platform is tested against current versions of:
        </p>
        <ul>
          <li>VoiceOver on macOS Safari and iOS Safari</li>
          <li>NVDA on Windows Firefox and Chromium</li>
          <li>TalkBack on Android Chrome</li>
          <li>Keyboard-only navigation across supported browsers</li>
          <li>Browser zoom at 200%</li>
        </ul>
        <p>
          If you use a different combination and hit a barrier, please tell us
          — that context is genuinely useful.
        </p>
      </section>

      <section id="report">
        <h2>6. Report a Barrier</h2>
        <p>
          If any part of emrkt.co is difficult or impossible for you to use,
          email{" "}
          <a href="mailto:info@emrkt.co">info@emrkt.co</a> with the subject
          line "Accessibility." Please include:
        </p>
        <ul>
          <li>The page URL and, if applicable, the specific element.</li>
          <li>The device, browser, and assistive technology in use.</li>
          <li>What you were trying to do and what happened instead.</li>
        </ul>
        <p>
          <strong>Acknowledgement window:</strong> we aim to reply within 3
          business days with next steps or a fix timeline. Critical blockers
          are prioritized against the current release.
        </p>
      </section>

      <section id="review">
        <h2>7. Review Cadence</h2>
        <p>
          This statement is reviewed at least quarterly and whenever a major
          release changes navigation, forms, or data-visualization surfaces.
          The date at the top reflects the most recent review.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default AccessibilityStatement;